import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Col, Row, Card, CardBody } from "reactstrap";
import { Field, reduxForm } from 'redux-form';
import { Table, TableBody, TableCell, TableRow, Radio, FormControlLabel, Button } from '@mui/material';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { showNotification, successNofication } from "src/shared/components/Notification";
import { Redirect } from 'react-router-dom';
import { RIDER_AVAILABILITY_STATUS_ON_DUTY, RIDER_AVAILABILITY_STATUS_OFF_DUTY, RIDER_AVAILABILITY_STATUS_ON_VACATION, ERROR_MESSAGES } from '../../../utils/pnd/PndConstants';
import renderDatePickerField from 'src/shared/components/form/DatePickerNew';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';

const UpdateRiderAvaiabilityStatus = ({ dataInfo, setLoading }) => {
    const [selectedStatus, setSelectedStatus] = useState(dataInfo?.riderAvailabilityId);
    const [isRedirect, setIsRedirect] = useState(false);
    const [isStartDate, setStartDate] = useState(dataInfo?.startDate || null);
    const [isEndDate, setEndDate] = useState(dataInfo?.endDate || null);

    if (isRedirect) {
        window.location.reload();
        return <Redirect to="/web_app/rider/availability" />;
    }

    const handleStatusChange = async (event) => {
        const newStatus = Number(event.target.value);
        if (dataInfo && dataInfo?.driverId) {
            try {
                let Obj = {
                    driverId: dataInfo?.driverId,
                };

                const response = await PndRestApi.CheckOrderAssignedToRider(Obj);
                if (response.status === 200 && response.data.status === 204) {
                    setSelectedStatus(newStatus);
                } else {
                    showNotification(
                        "theme-light",
                        "ltr",
                        `SuccessCode: ${response.data.status}`,
                        `Message:${response.data.message}`,
                    );
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                showNotification(
                    "theme-light",
                    "ltr",
                    `SuccessCode: 500`,
                    ERROR_MESSAGES.tryCatchError,
                );
            }
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            let Obj;
            if (selectedStatus !== RIDER_AVAILABILITY_STATUS_ON_DUTY) {
                // Use selected dates if the user changed them; otherwise fall back to existing dates from dataInfo
                const start = isStartDate ?? dataInfo?.startDate;
                const end = isEndDate ?? dataInfo?.endDate;
                if (!start || !end) {
                    showNotification(
                        "theme-light",
                        "ltr",
                        `SuccessCode: 500.`,
                        `Message: Start Date or End Date is required.`,
                    );
                    setLoading(false);
                    return;
                }
                const formattedStart = start.includes('T') ? start : `${start}T00:00:00`;
                const formattedEnd = end.includes('T') ? end : `${end}T23:59:59`;
                Obj = {
                    riderId: dataInfo?.riderId,
                    availabilityStatusId: selectedStatus,
                    startDate: formattedStart,
                    endDate: formattedEnd,
                };
            } else {
                Obj = {
                    riderId: dataInfo?.riderId,
                    availabilityStatusId: selectedStatus,
                };
            }
            const response = await PndRestApi.UpdateRiderAvailabilityStatus(Obj);
            if (response.status === 200 && response.data.status === 200) {
                successNofication(
                    "theme-light",
                    "ltr",
                    `SuccessCode: ${response.data.status}`,
                    `Message: Successfully updated the Rider Availability Details`,
                );
                setTimeout(() => {
                    setIsRedirect(true);
                }, 1000);
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `SuccessCode: ${response.data.status}`,
                    `Message: ${response.data.message}`,
                );
                setLoading(false);
            }
        } catch (error) {
            showNotification(
                "theme-light",
                "ltr",
                `SuccessCode: 500`,
                ERROR_MESSAGES.tryCatchError,
            );
            setLoading(false);
        }
    };

    return (
        <form className="p-1">
            <Card className="border-0 shadow-sm mb-4">
                <CardBody className="p-3 bg-light rounded shadow-sm border">
                    <div className="d-flex align-items-center mb-2">
                        <PersonIcon className="text-primary me-2" style={{ fontSize: '1.2rem' }} />
                        <h6 className="m-0 fw-bold text-dark">Rider: <span className="text-primary">{dataInfo?.riderName}</span></h6>
                    </div>
                    <div className="d-flex align-items-center">
                        <InfoIcon className="text-info me-2" style={{ fontSize: '1.2rem' }} />
                        <h6 className="m-0 fw-bold text-dark">Current Status: <span className="badge bg-soft-info text-info border px-2 py-1">{dataInfo?.riderAvailabilityDesc}</span></h6>
                    </div>
                </CardBody>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
                <CardBody className="p-0">
                    <Table size="small" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                        <TableBody>
                            <TableRow style={{ background: '#f8f9fa' }}>
                                <TableCell className="border-0 rounded-start">
                                    <FormControlLabel
                                        control={<Radio color="success" />}
                                        label={<span className="fw-bold small">ON DUTY</span>}
                                        value={RIDER_AVAILABILITY_STATUS_ON_DUTY}
                                        checked={selectedStatus === RIDER_AVAILABILITY_STATUS_ON_DUTY}
                                        onChange={handleStatusChange}
                                    />
                                </TableCell>
                                <TableCell className="border-0">
                                    <FormControlLabel
                                        control={<Radio color="warning" />}
                                        label={<span className="fw-bold small">OFF DUTY</span>}
                                        value={RIDER_AVAILABILITY_STATUS_OFF_DUTY}
                                        checked={selectedStatus === RIDER_AVAILABILITY_STATUS_OFF_DUTY}
                                        onChange={handleStatusChange}
                                    />
                                </TableCell>
                                <TableCell className="border-0 rounded-end">
                                    <FormControlLabel
                                        control={<Radio color="error" />}
                                        label={<span className="fw-bold small">ON VACATION</span>}
                                        value={RIDER_AVAILABILITY_STATUS_ON_VACATION}
                                        checked={selectedStatus === RIDER_AVAILABILITY_STATUS_ON_VACATION}
                                        onChange={handleStatusChange}
                                    />
                                </TableCell>
                            </TableRow>
                            {selectedStatus !== RIDER_AVAILABILITY_STATUS_ON_DUTY && (
                                <TableRow>
                                    <TableCell colSpan={3} className="border-0 pt-3">
                                        <Card className="bg-light border shadow-none">
                                            <CardBody className="p-3">
                                                <Row className="g-3">
                                                    <Col md={6}>
                                                        <div className="mb-1">
                                                            <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem' }}>
                                                                Start Date <span className='text-danger'>*</span>
                                                            </label>
                                                            <Field
                                                                name="startDate"
                                                                component={renderDatePickerField}
                                                                setDate={setStartDate}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-1">
                                                            <label className="form-label fw-bold text-muted small mb-1" style={{ fontSize: '0.75rem' }}>
                                                                End Date <span className='text-danger'>*</span>
                                                            </label>
                                                            <Field
                                                                name="endDate"
                                                                component={renderDatePickerField}
                                                                setDate={setEndDate}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardBody>
            </Card>

            <div className="d-flex justify-content-end pt-2 mt-2 border-top">
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSubmit}
                    startIcon={<SaveIcon sx={{ fontSize: '0.75rem !important' }} />}
                    sx={{
                        borderRadius: '3px',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '2px 8px',
                        fontSize: '10px',
                        minWidth: 'auto',
                        height: '22px',
                        lineHeight: '1',
                        background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                        border: 'none',
                        '&:hover': {
                            boxShadow: '0 1px 3px rgba(17, 153, 142, 0.2)'
                        }
                    }}
                >
                    Update Availability
                </Button>
            </div>
        </form>
    );
};

UpdateRiderAvaiabilityStatus.propTypes = {
    dataInfo: PropTypes.object.isRequired,
    setLoading: PropTypes.func.isRequired,
};

const mapStateToProps = (state, data) => {
    if (data && data?.dataInfo) {
        return {
            initialValues: {
                startDate: data?.dataInfo?.startDate,
                endDate: data?.dataInfo?.endDate,
            }
        }
    }
}

export default connect(mapStateToProps)(reduxForm({ form: 'UpdateRiderAvaiabilityStatusForm', enableReinitialize: true })(UpdateRiderAvaiabilityStatus));
